/**
 * Device-local location acquisition seam (T12.6.2).
 *
 * This is the only web-app module that reaches the geolocation API — browser
 * `navigator.geolocation` on web, `@capacitor/geolocation` on native. Coordinates
 * stay on-device in a short-lived local cache; consumers only receive a typed
 * outcome, never platform-specific errors or permission codes.
 *
 * Native permission state is read from the plugin's own `checkPermissions` /
 * `requestPermissions` (never asked vs denied vs granted) instead of being
 * inferred from a failed fix — the WebView's `navigator.permissions.query` is
 * unreliable on native and the per-origin browser grant T50.1 changed is not
 * the same thing as an OS-level permission grant. `@capacitor/geolocation` is
 * dynamically imported so the plugin never reaches the eager web bundle
 * (mirrors `useNativeAppVersion.ts`'s pattern for native-only plugins).
 */

import { Capacitor } from '@capacitor/core';
import type { GeolocationPlugin, PermissionStatus, Position as NativePosition } from '@capacitor/geolocation';
import { captureLocationOutcome, type AnalyticsPlatform } from '../analytics';

const CACHE_KEY = 'shopista.device-location.v1';

export type LocationPurpose = 'settings' | 'mode-detection';

export interface DeviceLocationFix {
  latitude: number;
  longitude: number;
  accuracy: number;
  timestamp: number;
  ageMs: number;
}

export type DeviceLocationOutcome =
  | { kind: 'ok'; fix: DeviceLocationFix; fromCache: boolean }
  | { kind: 'denied' }
  | { kind: 'unavailable' }
  | { kind: 'timeout'; partial?: DeviceLocationFix }
  | { kind: 'unsupported' };

export interface DeviceLocationRequest {
  purpose: LocationPurpose;
  maxAgeMs: number;
  accuracyTargetM: number;
  deadlineMs: number;
  /** Receives the live result after an immediate cache hit. */
  onRefresh?: (outcome: DeviceLocationOutcome) => void;
}

interface CachedFix {
  latitude: number;
  longitude: number;
  accuracy: number;
  timestamp: number;
}

let memoryCache: CachedFix | null = null;
let inFlight: Promise<DeviceLocationOutcome> | null = null;

function platform(): AnalyticsPlatform {
  const value = Capacitor.getPlatform();
  return value === 'android' || value === 'ios' ? value : 'web';
}

function emit(request: DeviceLocationRequest, outcome: DeviceLocationOutcome, startedAt: number): void {
  const fix = outcome.kind === 'ok' ? outcome.fix : outcome.kind === 'timeout' ? outcome.partial : undefined;
  captureLocationOutcome({
    surface: request.purpose,
    platform: platform(),
    outcome: outcome.kind,
    elapsed_ms: Math.max(0, Date.now() - startedAt),
    accuracy_m: fix?.accuracy ?? null,
    from_cache: outcome.kind === 'ok' ? outcome.fromCache : false,
    retry_count: 0,
  });
}

function toFix(cached: CachedFix): DeviceLocationFix {
  return { ...cached, ageMs: Math.max(0, Date.now() - cached.timestamp) };
}

function readCache(): CachedFix | null {
  if (memoryCache) return memoryCache;
  try {
    const raw = window.localStorage.getItem(CACHE_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw) as Partial<CachedFix>;
    if (
      typeof parsed.latitude !== 'number' || typeof parsed.longitude !== 'number' ||
      typeof parsed.accuracy !== 'number' || typeof parsed.timestamp !== 'number'
    ) return null;
    memoryCache = parsed as CachedFix;
    return memoryCache;
  } catch {
    return null;
  }
}

function saveCache(fix: CachedFix): void {
  memoryCache = fix;
  try {
    window.localStorage.setItem(CACHE_KEY, JSON.stringify(fix));
  } catch {
    // Storage is an optimisation only (privacy modes may reject writes).
  }
}

function outcomeFromError(code: number, partial?: DeviceLocationFix): DeviceLocationOutcome {
  if (code === 1) return { kind: 'denied' };
  if (code === 2) return { kind: 'unavailable' };
  return { kind: 'timeout', ...(partial ? { partial } : {}) };
}

/**
 * Native plugin error code for "could not obtain a location before its own
 * internal timeout elapsed" (Android/iOS — see @capacitor/geolocation's
 * documented error table). Unlike the browser's numeric `PositionError.code`,
 * the plugin's `err` is untyped (`any`) and carries this string code.
 */
const NATIVE_TIMEOUT_CODE = 'OS-PLUG-GLOC-0010';

function isNativeTimeoutError(err: unknown): boolean {
  return typeof err === 'object' && err !== null && (err as { code?: unknown }).code === NATIVE_TIMEOUT_CODE;
}

function startBrowserAcquisition(request: DeviceLocationRequest): Promise<DeviceLocationOutcome> {
  if (typeof navigator === 'undefined' || !navigator.geolocation) {
    return Promise.resolve({ kind: 'unsupported' });
  }

  // Real browsers expose watchPosition. The one-shot fallback only supports
  // older embedded implementations; production-capable browsers take the
  // progressive watch/deadline path below.
  if (typeof navigator.geolocation.watchPosition !== 'function') {
    return new Promise((resolve) => navigator.geolocation.getCurrentPosition(
      (position) => {
        const fix: DeviceLocationFix = {
          latitude: position.coords.latitude, longitude: position.coords.longitude,
          accuracy: position.coords.accuracy, timestamp: position.timestamp,
          ageMs: Math.max(0, Date.now() - position.timestamp),
        };
        saveCache(fix);
        resolve({ kind: 'ok', fix, fromCache: false });
      },
      (error) => resolve(outcomeFromError(error.code)),
      { enableHighAccuracy: false, maximumAge: request.maxAgeMs, timeout: request.deadlineMs },
    ));
  }

  return new Promise((resolve) => {
    let settled = false;
    let watchId: number | null = null;
    let best: DeviceLocationFix | undefined;
    const finish = (outcome: DeviceLocationOutcome): void => {
      if (settled) return;
      settled = true;
      if (watchId !== null) navigator.geolocation.clearWatch(watchId);
      resolve(outcome);
    };
    const deadline = window.setTimeout(() => finish({ kind: 'timeout', ...(best ? { partial: best } : {}) }), request.deadlineMs);
    const complete = (outcome: DeviceLocationOutcome): void => {
      window.clearTimeout(deadline);
      finish(outcome);
    };
    watchId = navigator.geolocation.watchPosition(
      (position) => {
        const candidate: DeviceLocationFix = {
          latitude: position.coords.latitude,
          longitude: position.coords.longitude,
          accuracy: position.coords.accuracy,
          timestamp: position.timestamp,
          ageMs: Math.max(0, Date.now() - position.timestamp),
        };
        if (!best || candidate.accuracy < best.accuracy) best = candidate;
        if (candidate.accuracy <= request.accuracyTargetM) {
          saveCache(candidate);
          complete({ kind: 'ok', fix: candidate, fromCache: false });
        }
      },
      (error) => complete(outcomeFromError(error.code, best)),
      // No `timeout` here: browsers don't reliably honour it for watchPosition
      // (unlike getCurrentPosition, where the option genuinely bounds a single
      // read), so it would only be a second deadline racing the one above at
      // the same value. Our own `deadline` timer is the sole owner and already
      // carries `best` and clears the watch.
      { enableHighAccuracy: false, maximumAge: request.maxAgeMs },
    );
    // Some legacy embedded implementations expose a no-op watchPosition but a
    // working one-shot API. Keep that compatibility seam out of callers.
    if (typeof watchId !== 'number') {
      window.clearTimeout(deadline);
      navigator.geolocation.getCurrentPosition(
        (position) => {
          const fix: DeviceLocationFix = {
            latitude: position.coords.latitude, longitude: position.coords.longitude,
            accuracy: position.coords.accuracy, timestamp: position.timestamp,
            ageMs: Math.max(0, Date.now() - position.timestamp),
          };
          saveCache(fix);
          finish({ kind: 'ok', fix, fromCache: false });
        },
        (error) => finish(outcomeFromError(error.code)),
        { enableHighAccuracy: false, maximumAge: request.maxAgeMs, timeout: request.deadlineMs },
      );
    }
  });
}

/**
 * Read native permission state and, if never asked, prompt exactly once. Uses
 * the `coarseLocation` alias throughout — this module never requests
 * high-accuracy fixes (every acquisition passes `enableHighAccuracy: false`),
 * so there is no reason to ask for `ACCESS_FINE_LOCATION`'s stricter dialog.
 * `checkPermissions`/`requestPermissions` throw when OS location services are
 * off (per the plugin's own contract) — that is a service-availability
 * problem, not a permission answer, so it maps to `unavailable`.
 */
async function resolveNativePermission(
  plugin: GeolocationPlugin,
): Promise<{ kind: 'granted' } | { kind: 'denied' } | { kind: 'unavailable' }> {
  let status: PermissionStatus;
  try {
    status = await plugin.checkPermissions();
  } catch {
    return { kind: 'unavailable' };
  }
  if (status.coarseLocation === 'granted') return { kind: 'granted' };
  if (status.coarseLocation === 'denied') return { kind: 'denied' };
  // 'prompt' | 'prompt-with-rationale' — never asked (or asked and dismissed
  // without a permanent denial). Trigger exactly one native prompt.
  try {
    const requested = await plugin.requestPermissions({ permissions: ['coarseLocation'] });
    return requested.coarseLocation === 'granted' ? { kind: 'granted' } : { kind: 'denied' };
  } catch {
    return { kind: 'unavailable' };
  }
}

function nativeFixFromPosition(position: NativePosition): DeviceLocationFix {
  return {
    latitude: position.coords.latitude,
    longitude: position.coords.longitude,
    accuracy: position.coords.accuracy,
    timestamp: position.timestamp,
    ageMs: Math.max(0, Date.now() - position.timestamp),
  };
}

/**
 * Native counterpart to `startBrowserAcquisition`: same progressive ladder
 * (accept the first fix meeting `accuracyTargetM`, else the best fix seen when
 * the deadline hits), but gated on a real permission read first so a denial
 * never depends on inferring anything from a failed fix.
 */
async function startNativeAcquisition(request: DeviceLocationRequest): Promise<DeviceLocationOutcome> {
  let plugin: GeolocationPlugin;
  try {
    ({ Geolocation: plugin } = await import('@capacitor/geolocation'));
  } catch {
    return { kind: 'unsupported' };
  }

  const permission = await resolveNativePermission(plugin);
  if (permission.kind !== 'granted') return { kind: permission.kind };

  return new Promise((resolve) => {
    let settled = false;
    let watchId: string | null = null;
    let best: DeviceLocationFix | undefined;
    const finish = (outcome: DeviceLocationOutcome): void => {
      if (settled) return;
      settled = true;
      if (watchId !== null) void plugin.clearWatch({ id: watchId });
      resolve(outcome);
    };
    const deadline = window.setTimeout(() => finish({ kind: 'timeout', ...(best ? { partial: best } : {}) }), request.deadlineMs);
    const complete = (outcome: DeviceLocationOutcome): void => {
      window.clearTimeout(deadline);
      finish(outcome);
    };
    plugin
      // Keep `timeout: request.deadlineMs` here (unlike the browser path):
      // the plugin has no "no timeout" mode — omitting it falls back to its
      // own hardcoded 10s default, an unrelated deadline that would fire
      // before our real one on any request with a longer deadlineMs. Passing
      // the same value means both timers race at the same instant instead;
      // the fix below is making sure whichever one wins still carries `best`.
      .watchPosition(
        { enableHighAccuracy: false, maximumAge: request.maxAgeMs, timeout: request.deadlineMs },
        (position, err) => {
          if (err) {
            complete(isNativeTimeoutError(err) ? { kind: 'timeout', ...(best ? { partial: best } : {}) } : { kind: 'unavailable' });
            return;
          }
          if (!position) {
            complete({ kind: 'unavailable' });
            return;
          }
          const candidate = nativeFixFromPosition(position);
          if (!best || candidate.accuracy < best.accuracy) best = candidate;
          if (candidate.accuracy <= request.accuracyTargetM) {
            saveCache(candidate);
            complete({ kind: 'ok', fix: candidate, fromCache: false });
          }
        },
      )
      .then((id) => {
        watchId = id;
        if (settled) void plugin.clearWatch({ id });
      })
      .catch(() => complete({ kind: 'unavailable' }));
  });
}

/** Platform seam: native reads the Capacitor plugin, web keeps `navigator.geolocation`. */
function startAcquisition(request: DeviceLocationRequest): Promise<DeviceLocationOutcome> {
  return Capacitor.isNativePlatform() ? startNativeAcquisition(request) : startBrowserAcquisition(request);
}

/** Acquire one device-local fix; concurrent callers share one watch. */
export function acquireDeviceLocation(request: DeviceLocationRequest): Promise<DeviceLocationOutcome> {
  const startedAt = Date.now();
  const cached = readCache();
  if (cached && Date.now() - cached.timestamp <= request.maxAgeMs) {
    const outcome: DeviceLocationOutcome = { kind: 'ok', fix: toFix(cached), fromCache: true };
    emit(request, outcome, startedAt);
    if (!inFlight) {
      inFlight = startAcquisition(request).finally(() => { inFlight = null; });
    }
    void inFlight.then((refreshed) => {
      emit(request, refreshed, startedAt);
      request.onRefresh?.(refreshed);
    });
    return Promise.resolve(outcome);
  }
  if (!inFlight) {
    inFlight = startAcquisition(request).finally(() => { inFlight = null; });
  }
  return inFlight.then((outcome) => {
    emit(request, outcome, startedAt);
    return outcome;
  });
}

/** Test-only cache reset. */
export function resetDeviceLocationForTest(): void {
  memoryCache = null;
  inFlight = null;
}

/** Test-only cache seeding that keeps the memory and localStorage layers aligned. */
export function seedDeviceLocationForTest(fix: Omit<CachedFix, 'timestamp'> & { timestamp?: number }): void {
  saveCache({ ...fix, timestamp: fix.timestamp ?? Date.now() });
}
